import type { Logger } from "@expert/logging";

import { removePii } from "@asurion-core/expert-workspace-pii";
import { sendEventMetric } from "@expert/cloudwatch-metrics";

import type { ShadowComparison, ShadowComparisonResult, ShadowComparisonStatus, ShadowDiffEntry } from "./types";

/** PII-safe projection of a comparison — paths only, never raw values. Safe to emit as an analytic. */
export function toShadowComparisonResult(comparison: ShadowComparison): ShadowComparisonResult {
    const diffPaths = comparison.diffs.map((diff) => diff.path);
    return {
        status: comparison.status,
        eventName: comparison.eventName,
        correlationId: comparison.correlationId,
        sessionId: comparison.sessionId,
        diffCount: diffPaths.length,
        diffPaths,
        latencyDeltaMs: comparison.latencyDeltaMs,
    };
}

/** CloudWatch metric name for a non-equal comparison status. */
export function metricNameForStatus(status: ShadowComparisonStatus): string {
    switch (status) {
        case "different":
            return "gaiaSimplr.mismatch";
        case "gaia-only":
            return "gaiaSimplr.gaiaOnly";
        case "simplr-only":
            return "gaiaSimplr.simplrOnly";
        default:
            return "gaiaSimplr.unknown";
    }
}

/**
 * Emits a GAIA-vs-Simplr comparison across three channels (matching the feature-flag
 * dark launch in `@asurion-core/expert-workspace-feature-flagging`):
 *  - **metric** — counts the mismatch by `eventName` (low-cardinality dimension only),
 *  - **log** — the differing fields with before/after values scrubbed of PII,
 *  - **analytic** — the PII-safe result (paths only) via `onComparison`.
 *
 * Equal comparisons are logged at `debug` only (no metric, no analytic) so volume stays bounded.
 */
export function reportShadowComparison(
    comparison: ShadowComparison,
    logger: Logger,
    onComparison?: (result: ShadowComparisonResult) => void,
): void {
    const result = toShadowComparisonResult(comparison);

    if (comparison.status === "equal") {
        logger.debug(toShadowLogFields(result), "GAIA/Simplr shadow | messages match");
        return;
    }

    // Count the mismatch (dimensions stay low-cardinality: event name only, never values/sessionId).
    void sendEventMetric(metricNameForStatus(comparison.status), { eventName: comparison.eventName });

    // Surface the PII-safe analytic (paths only).
    onComparison?.(result);

    if (comparison.status === "different" && comparison.diffs.length > 0) {
        // Log the differing fields with values scrubbed of PII so they are searchable in OpenSearch.
        void logRedactedDiffs(comparison, logger);
    } else {
        logger.info(toShadowLogFields(result), `GAIA/Simplr shadow | ${comparison.status}`);
    }
}

/**
 * Log payload for a {@link ShadowComparisonResult}. The status goes under `shadowStatus` because `status` is a
 * reserved log field; `onComparison` still receives `status`.
 */
function toShadowLogFields(
    result: ShadowComparisonResult,
): Omit<ShadowComparisonResult, "status"> & { shadowStatus: ShadowComparisonStatus } {
    const { status, ...rest } = result;
    return { ...rest, shadowStatus: status };
}

async function logRedactedDiffs(comparison: ShadowComparison, logger: Logger): Promise<void> {
    try {
        const diffs = await Promise.all(
            comparison.diffs.map(async (diff) => ({
                path: diff.path,
                gaia: await redactValue(diff.gaiaValue),
                simplr: await redactValue(diff.simplrValue),
            })),
        );
        logger.warn(
            {
                eventName: comparison.eventName,
                sessionId: comparison.sessionId,
                correlationId: comparison.correlationId,
                shadowStatus: comparison.status,
                latencyDeltaMs: comparison.latencyDeltaMs,
                diffs,
            },
            "GAIA/Simplr shadow | fields differ",
        );
    } catch (error: unknown) {
        logger.warn({ error, eventName: comparison.eventName }, "GAIA/Simplr shadow | failed to log field diffs");
    }
}

async function redactValue(value: ShadowDiffEntry["gaiaValue"]): Promise<string | undefined> {
    if (value === undefined) return undefined;
    const text = typeof value === "string" ? value : JSON.stringify(value);
    if (text === undefined) return undefined;
    return removePii(text);
}
